import { Injectable } from '@angular/core'

import { Paginator } from '../../../shared/interfaces/paginator.interface'
import { CatalogueService } from '../../../shared/services/catalogue.service'
import { Metric } from '../../tools-catalogue/interfaces/metric.interface'
import { MetricDto } from '../../tools-catalogue/resources/metric/dtos/metric.dto'
import { CatalogueResourceSlug } from '../enums/catalogue-resource-slug.enum'

@Injectable({
  providedIn: 'root'
})
export class MetricService {
  constructor(private catalogueService: CatalogueService) {}

  list(listParams: {
    terms?: string
    page?: number
    orderBy?: string
    orderByDesc?: string
    toolIds?: number[]
    licensingRegimeIds?: number[]
    organisationIds?: number[]
    objectiveIds?: number[]
    metricTypeIds?: number[]
    withoutPagination?: boolean
    toXLS?: string
  }): Promise<Paginator<Metric> | Metric[]> {
    const params = Object.assign({}, listParams)

    if (params.withoutPagination) {
      params.withoutPagination = true
    }

    if (params.orderBy) {
      if (params.orderBy === 'nameAsc') {
        params.orderBy = 'name'
      }
      if (params.orderBy === 'nameDesc') {
        params.orderBy = 'name'
        params.orderByDesc = 'true'
      }
    }

    if (params.withoutPagination) {
      return this.catalogueService.list('metrics', params) as Promise<Metric[]>
    } else {
      return (
        this.catalogueService.list('metrics', params) as Promise<
          Paginator<Metric>
        >
      ).then((paginatorRes: Paginator<Metric>) =>
        Object.assign(
          paginatorRes,
          paginatorRes.data ? paginatorRes.data : { data: [] }
        )
      )
    }
  }

  listPaginated(
    listParams: { [key: string]: any } = {}
  ): Promise<Paginator<Metric>> {
    listParams.withoutPagination = false
    return this.list(listParams) as Promise<Paginator<Metric>>
  }

  listUnpaginated(listParams: { [key: string]: any } = {}): Promise<Metric[]> {
    listParams.withoutPagination = true
    return this.list(listParams) as Promise<Metric[]>
  }

  // List all metrics available. As this cannot be done in Wordpress, we need to keep requesting the API until there is no more data.
  listAll(
    queryParams: { [key: string]: any },
    numberOfPages: number
  ): Promise<Metric[]> {
    return Array.from({ length: numberOfPages }, (_, i) => i + 1).reduce(
      (acc: Promise<Metric[]>, pageNumber: number) => {
        return acc.then((metrics: Metric[]) => {
          return this.listPaginated(
            Object.assign(queryParams, { page: pageNumber })
          ).then(
            (paginator: Paginator<Metric>) => {
              return metrics.concat(paginator.data)
            },
            // On error we still return the previous data.
            () => metrics
          )
        })
      },
      Promise.resolve([])
    )
  }

  show(slug: string): Promise<Metric> {
    return this.catalogueService.showBySlug(
      CatalogueResourceSlug.Metrics,
      slug
    ) as Promise<Metric>
  }

  store(metricDto: MetricDto) {
    return this.catalogueService.store(CatalogueResourceSlug.Metrics, metricDto)
  }
}
